import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type {
  AskLookalikePollDto,
  CreditBalance,
  ResearchPollDraft,
  ResearchReportView,
  ResearchRoomEvent,
  ResearchRoomFollowUpMode,
  ResearchRoomMessage,
  ResearchRoomView,
} from '@repo/contracts';

import { trackEvent } from '../analytics/posthog';
import { ApiError, creditShortfall } from '../api/client';
import {
  askLookalikePoll as askLookalikePollApi,
  askRoom,
  createRoom,
  endRoom,
  getRoomForCapture,
  replayRoom,
  reportKeys,
  researchStatusKeys,
  roomKeys,
} from '../api/research-rooms';
import { useLiveQuery } from '../api/use-live-query';
import { creditKeys, invalidateCredits } from '../credits/use-credits';
import { logger } from '../lib/logger';
import { useResearchRoomStore } from '../store/research-room-store';
import { PERSONA_MODEL_PICKER_ENABLED } from './persona-model-picker-enabled';

const log = logger.child({ module: 'research-room' });

function inOrder(a: ResearchRoomMessage, b: ResearchRoomMessage): number {
  return a.round - b.round || a.index - b.index;
}

/** Adds a message to a room, or replaces it if it is already there. */
export function upsertMessage(
  view: ResearchRoomView,
  message: ResearchRoomMessage,
): ResearchRoomView {
  const messages = view.messages.filter((m) => m.id !== message.id);
  return { ...view, messages: [...messages, message].sort(inOrder) };
}

function reasonOf(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) {
    return 'The room is still answering — try again in a moment.';
  }
  return err instanceof Error ? err.message : 'Something went wrong';
}

/** The balance after a charge, moved in place: base first, as the server
 *  spends it, so the split stays plausible until the refetch lands. */
export function afterCharge(
  balance: CreditBalance,
  remaining: number,
): CreditBalance {
  const spent = Math.max(0, balance.totalCredits - remaining);
  const baseUsed = Math.min(balance.baseCredits, spent);
  return {
    baseCredits: balance.baseCredits - baseUsed,
    topupCredits: Math.max(0, balance.topupCredits - (spent - baseUsed)),
    totalCredits: remaining,
  };
}

/**
 * The research room open on one capture.
 *
 * The room and its finished messages are one query entry. Two things write
 * it: the stream a question answers with (fast, only in the tab that asked)
 * and the room's live query (for a reload mid-round or a second tab). The
 * live query stands down while this tab is asking, because Firestore trails
 * the answer stream by a write or two and would briefly roll the room back.
 *
 * What a speaker has said *so far* is not in the cache: it exists only on the
 * stream until the line is finished, so it is kept here as drafts keyed by
 * message id and dropped as each message lands whole.
 */
export function useResearchRoom(captureId: string | null) {
  const queryClient = useQueryClient();
  const key = roomKeys.forCapture(captureId ?? 'none');

  const query = useQuery({
    enabled: Boolean(captureId),
    queryFn: () => getRoomForCapture(captureId!),
    queryKey: key,
  });
  const view = query.data ?? null;
  const roomId = view?.room.id;

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  /** The question on its way, drawn until the server's copy arrives. */
  const [sending, setSending] = useState<{
    poll?: ResearchPollDraft;
    targets: string[];
    text: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** A question refused for want of credits: what it needed and what there
   *  was. Its own state, not an `error` — the composer explains it and
   *  offers a top-up rather than showing a message. */
  const [shortfall, setShortfall] = useState<{
    available: number;
    required: number;
  } | null>(null);

  /** Refused before anything ran: a shortfall is explained, anything else
   *  is said. */
  const refused = (err: unknown) => {
    const short = creditShortfall(err);
    if (short) {
      setShortfall(short);
      setError(null);
    } else {
      setError(reasonOf(err));
    }
  };

  const write = (message: ResearchRoomMessage) => {
    queryClient.setQueryData<ResearchRoomView | null>(key, (current) =>
      current ? upsertMessage(current, message) : current,
    );
  };

  /** Folds one frame of a room's stream into what is on screen. Returns
   *  whether it was the question itself, or Floto holding it back. */
  const receive = (event: ResearchRoomEvent): boolean => {
    if (event.type === 'message') {
      write(event.message);
      if (event.message.status !== 'pending') {
        setDrafts(({ [event.message.id]: _, ...rest }) => rest);
      }
      return (
        event.message.kind === 'question' ||
        event.message.kind === 'poll' ||
        event.message.kind === 'nudge'
      );
    }
    if (event.type === 'delta') {
      setDrafts((current) => ({
        ...current,
        [event.messageId]: (current[event.messageId] ?? '') + event.text,
      }));
    } else if (event.type === 'error') {
      setError(event.message);
    } else if (event.type === 'charge') {
      // The price lands on the question it paid for, and the balance moves
      // now rather than on the next refetch — watching it not move reads as
      // the round having been free.
      queryClient.setQueryData<ResearchRoomView | null>(key, (current) => {
        const opened = current?.messages.find((m) => m.id === event.messageId);
        return current && opened
          ? upsertMessage(current, { ...opened, credits: event.credits })
          : current;
      });
      queryClient.setQueryData<CreditBalance>(
        creditKeys.balance(),
        (current) =>
          current ? afterCharge(current, event.remaining) : current,
      );
    } else if (event.type === 'report') {
      // The first report frame is the session ending: close the room here
      // without waiting for its document to come back round.
      queryClient.setQueryData<ResearchRoomView | null>(key, (current) =>
        current && !current.room.endedAt
          ? {
              ...current,
              room: { ...current.room, endedAt: event.report.startedAt },
            }
          : current,
      );
      queryClient.setQueryData<ResearchReportView>(
        reportKeys.forRoom(event.report.roomId),
        (current) => (current ? { ...current, report: event.report } : current),
      );
    }
    return false;
  };

  const ask = useMutation({
    mutationFn: async ({
      about,
      independent = false,
      poll,
      skipCheck = false,
      targets,
      text,
      via,
    }: {
      about?: { messageId: string; mode: ResearchRoomFollowUpMode };
      independent?: boolean;
      poll?: ResearchPollDraft;
      skipCheck?: boolean;
      targets: string[];
      text: string;
      /**
       * How the question came to be sent, for analytics only: `typed`,
       * `idea` (Floto's, unedited), `nudge_anyway`, `nudge_poll`,
       * `follow_up`, `dissent`. Every path into the room goes through here,
       * so this is the one place a question is counted.
       */
      via?: string;
    }) => {
      if (!roomId) throw new Error('No room open');
      trackEvent('room_question_asked', {
        follow_up: about?.mode,
        independent,
        mentions: targets.length,
        mode: about ? 'follow_up' : poll ? 'poll' : 'question',
        poll_format: poll?.format,
        poll_options: poll?.options.length,
        room_id: roomId,
        round: view?.room.round,
        source: via ?? 'typed',
      });
      // A pick saved while the picker was showing is ignored once it is
      // hidden: everyone gets the default.
      const personaModel = PERSONA_MODEL_PICKER_ENABLED
        ? useResearchRoomStore.getState().personaModel
        : null;
      setError(null);
      setShortfall(null);
      setSending({ poll, targets, text });
      /** Whether the server has the question (stored, or held in a nudge). */
      let landed = false;
      /**
       * Settled at `ready` — everyone has answered and the room is free —
       * not at the end of the stream: Floto's read, the last reactions and
       * the fix list still arrive after it, and the next question should not
       * wait on them. The stream carries on; its end refreshes what it wrote.
       */
      return new Promise<void>((resolve, reject) => {
        let settled = false;
        const settle = () => {
          if (settled) return false;
          settled = true;
          resolve();
          return true;
        };
        askRoom(
          roomId,
          {
            text,
            ...(targets.length > 0 ? { targets } : {}),
            ...(skipCheck ? { skipCheck } : {}),
            ...(poll ? { poll } : {}),
            ...(about ? { about } : {}),
            ...(independent ? { independent } : {}),
            // Only ever set by an admin — see the composer's model picker.
            ...(personaModel ? { model: personaModel } : {}),
          },
          (event: ResearchRoomEvent) => {
            // Either the question landed, or Floto held it back — both
            // replace the bubble drawn while it was on its way.
            if (receive(event)) {
              landed = true;
              setSending(null);
            }
            if (event.type === 'message' && event.message.kind === 'nudge') {
              trackEvent('room_question_held', {
                reason: event.message.nudge?.reason,
                room_id: roomId,
              });
            }
            if (event.type === 'error') {
              trackEvent('room_question_failed', {
                reason: 'stream',
                room_id: roomId,
              });
            }
            // A round that fails before the question is stored would lose
            // it — the composer was cleared on send. Put it back.
            if (event.type === 'error' && !landed) {
              useResearchRoomStore.getState().setDraft(text);
            }
            if (event.type === 'ready') settle();
          },
        ).then(
          () => {
            // Ended after `ready`: the rest of the round arrived since.
            if (!settle()) {
              void queryClient.invalidateQueries({ queryKey: key });
              invalidateCredits(queryClient);
            }
          },
          (err: unknown) => {
            if (settled) {
              log.warn(
                { err },
                'the round ended badly after the room was free',
              );
              return;
            }
            settled = true;
            reject(err instanceof Error ? err : new Error(String(err)));
          },
        );
      });
    },
    onError: (err, { text }) => {
      log.warn({ err }, 'could not ask the room');
      trackEvent('room_question_failed', {
        reason: creditShortfall(err)
          ? 'credits'
          : err instanceof ApiError && err.status === 409
            ? 'busy'
            : 'error',
        room_id: roomId ?? undefined,
      });
      refused(err);
      // Refused outright (409, a dropped connection): the question never got
      // anywhere, so it goes back in the composer to send again.
      if (!useResearchRoomStore.getState().draft) {
        useResearchRoomStore.getState().setDraft(text);
      }
    },
    onSettled: () => {
      setSending(null);
      setDrafts({});
      useResearchRoomStore.getState().endAskedRound();
      // Whatever the stream missed, Firestore has.
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: researchStatusKeys.all });
      invalidateCredits(queryClient);
    },
  });

  /** A re-test's replay: every question again, then the comparison. */
  const replay = useMutation({
    mutationFn: async () => {
      if (!roomId) throw new Error('No room open');
      setError(null);
      setShortfall(null);
      await replayRoom(roomId, receive);
    },
    onError: (err) => {
      log.warn({ err }, 'could not run the re-test');
      trackEvent('retest_failed', {
        reason: creditShortfall(err) ? 'credits' : 'replay',
        room_id: roomId ?? undefined,
      });
      refused(err);
    },
    onSettled: () => {
      setDrafts({});
      void queryClient.invalidateQueries({ queryKey: key });
      invalidateCredits(queryClient);
    },
  });

  /** Ends the session: the closing summary, then the final report. */
  const end = useMutation({
    mutationFn: async () => {
      if (!roomId) throw new Error('No room open');
      setError(null);
      await endRoom(roomId, receive);
    },
    onError: (err) => {
      log.warn({ err }, 'could not end the session');
      trackEvent('room_end_failed', { room_id: roomId ?? undefined });
      setError(reasonOf(err));
    },
    onSettled: () => {
      setDrafts({});
      void queryClient.invalidateQueries({ queryKey: key });
      // Every list that shows where sessions stand — the project's cards,
      // the dashboard — now reads "report ready" or "writing".
      void queryClient.invalidateQueries({ queryKey: researchStatusKeys.all });
      if (roomId) {
        void queryClient.invalidateQueries({
          queryKey: reportKeys.forRoom(roomId),
        });
      }
    },
  });

  /** The lookalike poll on its way, drawn in the transcript until the real
   *  result (or a failure) replaces it — the room never looks like the
   *  question went nowhere while a spec generates and Jev works through it. */
  const [lookalikeSending, setLookalikeSending] =
    useState<AskLookalikePollDto | null>(null);

  /** A yes/no question, put to one persona's lookalike population — outside
   *  the room's own turns, so one request/response rather than a stream. */
  const lookalikePoll = useMutation({
    mutationFn: (dto: AskLookalikePollDto) => {
      if (!roomId) throw new Error('No room open');
      trackEvent('room_question_asked', {
        count: dto.count,
        mode: 'lookalike_poll',
        room_id: roomId,
        source: 'typed',
      });
      setError(null);
      setLookalikeSending(dto);
      return askLookalikePollApi(roomId, dto);
    },
    onError: (err, dto) => {
      log.warn({ err }, 'could not run the lookalike poll');
      trackEvent('lookalike_poll_failed', {
        reason: creditShortfall(err) ? 'credits' : 'error',
        room_id: roomId ?? undefined,
      });
      setError(reasonOf(err));
      // The composer already cleared the question on submit — put it back
      // so a refused ask (not yes/no, no credits, Jev unavailable) isn't a
      // silent no-op the researcher has to retype from memory.
      if (!useResearchRoomStore.getState().draft) {
        useResearchRoomStore.getState().setDraft(dto.question);
      }
    },
    onSuccess: (message) => {
      trackEvent('lookalike_poll_completed', { room_id: roomId ?? undefined });
      write(message);
    },
    onSettled: () => {
      setLookalikeSending(null);
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  useLiveQuery<ResearchRoomView>({
    enabled:
      Boolean(roomId) &&
      !ask.isPending &&
      !replay.isPending &&
      !end.isPending &&
      !lookalikePoll.isPending,
    path: `/canvas/research-rooms/${roomId ?? ''}/stream`,
    queryKey: key,
  });

  const start = useMutation({
    mutationFn: () => createRoom(captureId!),
    onError: (err) => {
      log.warn({ err }, 'could not open a research room');
      trackEvent('room_open_failed', {
        status: err instanceof ApiError ? String(err.status) : 'network',
      });
      setError(reasonOf(err));
    },
    onSuccess: (created) => {
      setError(null);
      queryClient.setQueryData<ResearchRoomView | null>(key, created);
      // The session's chat is now this room: the panel keeps showing it
      // whichever screen is selected next.
      queryClient.setQueryData<ResearchRoomView | null>(
        roomKeys.forSession(created.room.sessionId),
        created,
      );
      void queryClient.invalidateQueries({ queryKey: researchStatusKeys.all });
    },
  });

  const thinking =
    ask.isPending ||
    replay.isPending ||
    end.isPending ||
    view?.room.status === 'thinking';

  return {
    ask: ask.mutate,
    askLookalikePoll: lookalikePoll.mutate,
    drafts,
    error,
    loading: query.isPending && Boolean(captureId),
    lookalikeSending,
    messages: view?.messages ?? [],
    replay: replay.mutate,
    room: view?.room ?? null,
    sending,
    shortfall,
    start: start.mutate,
    starting: start.isPending,
    thinking,
    end: end.mutate,
    ending: end.isPending,
  };
}
